package meineglasfaser.frontend.www.pages.order

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.role
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.navigation.Anchor
import io.ktor.client.call.*
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.minus
import kotlinx.datetime.todayIn
import meineglasfaser.api.Customer
import meineglasfaser.api.CustomerAccountInfo
import meineglasfaser.api.CustomerType
import meineglasfaser.api.ShopBasket
import meineglasfaser.api.routes.CustomerPortalRoutes
import meineglasfaser.api.routes.ShopRoutes
import meineglasfaser.frontend.common.BackendApi
import meineglasfaser.frontend.common.components.widgets.Toast
import meineglasfaser.frontend.common.components.widgets.Toasts
import meineglasfaser.frontend.common.components.widgets.UBLPostalAddressInputs
import meineglasfaser.frontend.common.components.widgets.rememberQuery
import meineglasfaser.frontend.common.components.yaform.YaForm
import meineglasfaser.frontend.common.components.yaform.YaFormButton
import meineglasfaser.frontend.common.components.yaform.YaFormCheckInput
import meineglasfaser.frontend.common.components.yaform.YaFormLocalDateInput
import meineglasfaser.frontend.common.components.yaform.YaFormTextInput
import meineglasfaser.frontend.common.setJsonBody
import meineglasfaser.frontend.common.withAuth
import meineglasfaser.frontend.www.components.LocalCustomerState
import meineglasfaser.frontend.www.components.ShopBasketDAO
import meineglasfaser.frontend.www.components.fetchComputedBasket
import meineglasfaser.frontend.www.components.sections.FooterLinks
import meineglasfaser.frontend.www.pages.BasketView
import meineglasfaser.frontend.www.pages.portal.EmailVerificationWarning
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.type
import org.jetbrains.compose.web.dom.*
import kotlin.time.Clock

@Page
@Layout(".components.layouts.ShopLayout")
@Composable
fun OrderPage() {
    val accountInfo = LocalCustomerState.current.accountInfoOrNull()
    val (basket, computedBasket) = fetchComputedBasket(includeTax = true)

    Section({ classes("container") }) {
        Header {
            H1 { Text("Bestellformular") }
        }
        if (accountInfo == null) {
            Article({ classes("bg-warn") }) {
                Header {
                    Text("Login erforderlich")
                }
                Span { Text("Ab hier beginnt Ihr exklusiver Kundenbereich, Bitte melden Sie sich an.") }
                Br()
                Br()
                Anchor("/kundenportal/auth/login?redirect=/order", { role("button") }) {
                    Text("Zum Login")
                }
                Br()
                Br()
                B { Text("Neu hier?") }
                Br()
                Br()
                Anchor("/kundenportal/auth/register?redirect=/order", { role("button") }) {
                    Text("Hier registrieren")
                }
            }
        } else if (!accountInfo.accountEmailVerified) {
            EmailVerificationWarning()
        } else if (computedBasket == null || computedBasket.products.isEmpty()) {
            Article({ classes("bg-warn") }) {
                Header {
                    Text("Warenkorb leer")
                }
                P { Text("Ihr Warenkorb ist leer. Bitte fügen Sie Produkte hinzu, bevor Sie eine Bestellung aufgeben.") }
                Anchor("/") {
                    Text("Zu den Produkten")
                }
            }
        } else {
            OrderForm(accountInfo, basket, computedBasket)
        }
    }
}

@Composable
private fun OrderForm(
    accountInfo: CustomerAccountInfo,
    basket: ShopBasket,
    computedBasket: ShopBasket.Computed,
) {
    val fullAccountInfo = rememberQuery<Customer?>(null) {
        BackendApi.get(CustomerPortalRoutes.Account.FullInfo()) {
            withAuth()
        }
    }
    if (fullAccountInfo == null) return

    val ctx = rememberPageContext()

    YaForm(
        initial = ShopRoutes.Order.RequestBody(
            basket = basket,
            individualFirstName = fullAccountInfo.individualFirstName,
            individualLastName = fullAccountInfo.individualLastName,
            individualDob = fullAccountInfo.individualDob,
            companyName = fullAccountInfo.companyName,
            companyVatNumber = fullAccountInfo.companyVatNumber,
            companyContactPerson = fullAccountInfo.companyContactPerson,
            telFixedLine = fullAccountInfo.telFixedLine,
            telMobile = fullAccountInfo.telMobile,
            invoicingAddressStreetName = fullAccountInfo.invoicingAddress?.streetName ?: "",
            invoicingAddressCityName = fullAccountInfo.invoicingAddress?.cityName ?: "",
            invoicingAddressPostalZone = fullAccountInfo.invoicingAddress?.postalCode ?: "",
            invoicingAddressCountryCode = fullAccountInfo.invoicingAddress?.countryCode ?: "AT",
            invoicingEmail = fullAccountInfo.invoicingEmail,
            invoicingAccountHolder = fullAccountInfo.invoicingAccountHolder ?: "",
            invoicingIBAN = fullAccountInfo.invoicingIBAN ?: "",
        ),
        onSubmitScope = { data ->
            val body = ShopRoutes.Order.RequestBody(
                basket = basket,
                individualFirstName = data.stringOrNull(ShopRoutes.Order.RequestBody::individualFirstName),
                individualLastName = data.stringOrNull(ShopRoutes.Order.RequestBody::individualLastName),
                individualDob = data.localDateOrNull(ShopRoutes.Order.RequestBody::individualDob),
                companyName = data.stringOrNull(ShopRoutes.Order.RequestBody::companyName),
                companyVatNumber = data.stringOrNull(ShopRoutes.Order.RequestBody::companyVatNumber),
                companyContactPerson = data.stringOrNull(ShopRoutes.Order.RequestBody::companyContactPerson),
                telFixedLine = data.stringOrNull(ShopRoutes.Order.RequestBody::telFixedLine),
                telMobile = data.stringOrNull(ShopRoutes.Order.RequestBody::telMobile),
                deliveryAddressStreetName = data.string(ShopRoutes.Order.RequestBody::deliveryAddressStreetName),
                deliveryAddressCityName = data.string(ShopRoutes.Order.RequestBody::deliveryAddressCityName),
                deliveryAddressPostalZone = data.string(ShopRoutes.Order.RequestBody::deliveryAddressPostalZone),
                deliveryAddressCountryCode = data.string(ShopRoutes.Order.RequestBody::deliveryAddressCountryCode),
                invoicingAddressStreetName = data.string(ShopRoutes.Order.RequestBody::invoicingAddressStreetName),
                invoicingAddressCityName = data.string(ShopRoutes.Order.RequestBody::invoicingAddressCityName),
                invoicingAddressPostalZone = data.string(ShopRoutes.Order.RequestBody::invoicingAddressPostalZone),
                invoicingAddressCountryCode = data.string(ShopRoutes.Order.RequestBody::invoicingAddressCountryCode),
                invoicingEmail = data.stringOrNull(ShopRoutes.Order.RequestBody::invoicingEmail),
                invoicingAccountHolder = data.string(ShopRoutes.Order.RequestBody::invoicingAccountHolder),
                invoicingIBAN = data.string(ShopRoutes.Order.RequestBody::invoicingIBAN),
                bankSepaConfirmation = data.boolean(ShopRoutes.Order.RequestBody::bankSepaConfirmation),
                termsConfirmation = data.boolean(ShopRoutes.Order.RequestBody::termsConfirmation),
                privacyConfirmation = data.boolean(ShopRoutes.Order.RequestBody::privacyConfirmation),
                placementConfirmation = data.boolean(ShopRoutes.Order.RequestBody::placementConfirmation),
            )
            BackendApi.post(ShopRoutes.Order()) {
                withAuth()
                setJsonBody(body)
            }.fold(
                onSuccess = { response ->
                    val responseBody = response.body<ShopRoutes.Order.ResponseBody>()
                    ShopBasketDAO.clear()
                    ctx.router.navigateTo("/order/success?nid=${responseBody.orderNid}")
                },
                onFailure = { error ->
                    Toasts.display(
                        Toast.error("Fehler beim Absenden der Bestellung: ${error.message}")
                    )
                }
            )
        }
    ) {
        Div({ classes("row-fluid") }) {
            Div({ classes("col-12", "col-md-6") }) {
                Article {
                    Header { Text("Persönliche Daten / Firmendaten") }
                    if (accountInfo.type == CustomerType.INDIVIDUAL) {
                        YaFormTextInput(
                            prop = ShopRoutes.Order.RequestBody::individualFirstName,
                            label = { Text("Vorname") },
                            type = InputType.Text,
                            required = true,
                        )
                        YaFormTextInput(
                            prop = ShopRoutes.Order.RequestBody::individualLastName,
                            label = { Text("Nachname") },
                            type = InputType.Text,
                            required = true,
                        )
                        YaFormLocalDateInput(
                            prop = ShopRoutes.Order.RequestBody::individualDob,
                            label = { Text("Geburtsdatum") },
                            min = LocalDate(1900, 1, 1),
                            max = Clock.System.todayIn(TimeZone.currentSystemDefault())
                                .minus(DatePeriod(years = 18)),
                            required = false,
                        )
                    } else if (accountInfo.type == CustomerType.COMPANY) {
                        YaFormTextInput(
                            prop = ShopRoutes.Order.RequestBody::companyName,
                            label = { Text("Firmenname") },
                            type = InputType.Text,
                            required = true,
                        )
                        YaFormTextInput(
                            prop = ShopRoutes.Order.RequestBody::companyVatNumber,
                            label = { Text("UID-Nr.") },
                            type = InputType.Text,
                            pattern = "ATU[0-9]{8}",
                            required = true,
                        )
                        YaFormTextInput(
                            prop = ShopRoutes.Order.RequestBody::companyContactPerson,
                            label = { Text("Ansprechpartner") },
                            type = InputType.Text,
                            required = false,
                        )
                    }
                    YaFormTextInput(
                        prop = ShopRoutes.Order.RequestBody::telFixedLine,
                        label = { Text("Festnetznummer") },
                        type = InputType.Tel,
                        required = false,
                    )
                    YaFormTextInput(
                        prop = ShopRoutes.Order.RequestBody::telMobile,
                        label = { Text("Mobilnummer") },
                        type = InputType.Tel,
                        required = false,
                    )
                    DataStoredInAccountFootnote()
                }
            }
            Div({ classes("col-12", "col-md-6") }) {
                Article {
                    Header { Text("Anschlussaddresse") }
                    UBLPostalAddressInputs(
                        streetName = ShopRoutes.Order.RequestBody::deliveryAddressStreetName,
                        cityName = ShopRoutes.Order.RequestBody::deliveryAddressCityName,
                        postalZone = ShopRoutes.Order.RequestBody::deliveryAddressPostalZone,
                        countryCode = ShopRoutes.Order.RequestBody::deliveryAddressCountryCode,
                    )
                }
                Article {
                    Header {
                        Text("Rechnungsadresse")
                    }
                    Button({
                        type(ButtonType.Button)
                        classes("primary", "outline")
                        onClick {
                            state.data[ShopRoutes.Order.RequestBody::deliveryAddressStreetName.name]?.let {
                                state.data[ShopRoutes.Order.RequestBody::invoicingAddressStreetName.name] = it
                            }
                            state.data[ShopRoutes.Order.RequestBody::deliveryAddressPostalZone.name]?.let {
                                state.data[ShopRoutes.Order.RequestBody::invoicingAddressPostalZone.name] = it
                            }
                            state.data[ShopRoutes.Order.RequestBody::deliveryAddressCityName.name]?.let {
                                state.data[ShopRoutes.Order.RequestBody::invoicingAddressCityName.name] = it
                            }
                            state.data[ShopRoutes.Order.RequestBody::deliveryAddressCountryCode.name]?.let {
                                state.data[ShopRoutes.Order.RequestBody::invoicingAddressCountryCode.name] = it
                            }
                        }
                    }) {
                        Text("Von Anschlussaddresse übernehmen")
                    }
                    UBLPostalAddressInputs(
                        streetName = ShopRoutes.Order.RequestBody::invoicingAddressStreetName,
                        cityName = ShopRoutes.Order.RequestBody::invoicingAddressCityName,
                        postalZone = ShopRoutes.Order.RequestBody::invoicingAddressPostalZone,
                        countryCode = ShopRoutes.Order.RequestBody::invoicingAddressCountryCode,
                    )
                    DataStoredInAccountFootnote()
                }
            }
        }

        Article {
            Header { Text("Bankdaten für SEPA-Lastschrift") }
            Div({ classes("row-fluid") }) {
                Div({ classes("col-12", "col-md-6") }) {
                    YaFormTextInput(
                        prop = ShopRoutes.Order.RequestBody::invoicingAccountHolder,
                        label = { Text("Kontoinhaber") },
                        type = InputType.Text,
                        required = true,
                    )
                }
                Div({ classes("col-12", "col-md-6") }) {
                    YaFormTextInput(
                        prop = ShopRoutes.Order.RequestBody::invoicingIBAN,
                        label = { Text("IBAN") },
                        type = InputType.Text,
                        pattern = "AT[0-9]{2}[0-9]{16}",
                        required = true,
                    )
                }
            }
            YaFormCheckInput(
                prop = ShopRoutes.Order.RequestBody::bankSepaConfirmation,
                label = { Text("Ich ermächtige / Wir ermächtigen Prometheus Network Zahlungen von meinem / unserem Konto mittels SEPA-Lastschrift einzuziehen. Zugleich weise ich mein / weisen wir unser Kreditinstitut an, die von Prometheus Network auf mein / unser Konto gezogenen SEPA Lastschriften einzulösen. Ich kann / Wir können innerhalb von acht Wochen, beginnend mit dem Belastungsdatum, die Erstattung des belasteten Betrages verlangen. Es gelten dabei die mit meinem / unserem Kreditinstitut vereinbarten Bedingungen.") },
                required = true,
            )
            DataStoredInAccountFootnote()
        }

        Article {
            Header { Text("Rechtliches") }
            YaFormCheckInput(
                prop = ShopRoutes.Order.RequestBody::termsConfirmation,
                required = true,
                label = {
                    Text("Ich habe die ")
                    Anchor(href = FooterLinks.TERMS_OF_SERVICE_URL.href, {
                    }) {
                        Text("Allgemeinen Geschäftsbedingungen")
                    }
                    Text(" gelesen und akzeptiere diese")
                },
            )
            YaFormCheckInput(
                prop = ShopRoutes.Order.RequestBody::privacyConfirmation,
                required = true,
                label = {
                    Text("Ich habe die Datenschutzerklärung (in den AGBs) gelesen und akzeptiere diese")
                },
            )
        }

        Article {
            Header { Text("Auftragserteilung") }
            YaFormCheckInput(
                prop = ShopRoutes.Order.RequestBody::placementConfirmation,
                label = {
                    Text("Ich bin damit einverstanden, dass Prometheus Network die einmalige Herstellungsgebühr ab erfolgter Herstellung von meinem Konto einzieht, sofern diese nicht im Rahmen einer Aktion erlassen wird und in der Folge die monatlichen Gesprächsgebühren bzw. monatlichen Fixentgelte auch einzieht. Bei Neuanmeldungen wird auch bei einem verkürzten Abrechnungszeitraum (z.B. bei einer Anmeldung in der Monatsmitte und Abrechnung vom 26. des Monats bis Monatsletzten) immer die volle Grundgebühr verrechnet. Die Bestellung erfolgt zu den derzeit gültigen Allgemeinen Geschäftsbedingungen, welche die Erbringung von Prometheus Network Internet- und Telefoniedienstleistungen regeln, sowie zu den aktuellen Entgelten und Tarifen von Prometheus Network. Ausschließlicher Gerichtsstand ist Graz. Für Verbraucher gilt § 14 KSchG. 24 Monate Vertragsbindung ab Herstellung bzw. Vertragsänderung, danach monatlich kündbar. Sollten Sie aufgrund des TKG §135 Abs. 1 eine verkürzte Vertragslaufzeit von 12 Monaten wünschen, vermerken Sie dies bitte in den Anmerkungen. Die Herstellung erfolgt vorbehaltlich technischer Realisierbarkeit. Die Internetleitung wird exklusiv für den Vertragsinhaber bereitgestellt und darf keines falls missbräuchlich verwendet werden. Des Weiteren besteht ein Verbot der Nutzungsweitergabe an Dritte. Mit meiner Unterschrift bestätige ich den Vertrag gelesen und verstanden zu haben sowie die Richtigkeit obiger Daten.")
                },
                required = true,
            )
        }

        Article {
            Header { Text("In der Bestellung enthaltene Produkte") }
            BasketView(computedBasket, showActionColumn = false)
        }

        Fieldset {
            YaFormButton(ButtonType.Submit) {
                Text("Kostenpflichtig bestellen")
            }
        }
    }
}

@Composable
private fun DataStoredInAccountFootnote() {
    Small {
        Text("Daten in diesem Block werden im Account hinterlegt.")
    }
}
